import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateCookiePreferences } from '../store/slices/cookieAcceptanceSlice';
import { initializeAnalytics, clearAllAnalyticsCookies } from '../utils/analyticsManager';

const CookiePreferences = () => {
  const dispatch = useDispatch();
  const { analyticsEnabled, gtmEnabled } = useSelector((state) => state.cookieAcceptance);
  
  const [localAnalyticsEnabled, setLocalAnalyticsEnabled] = useState(analyticsEnabled);
  const [localGTMEnabled, setLocalGTMEnabled] = useState(gtmEnabled);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setLocalAnalyticsEnabled(analyticsEnabled);
    setLocalGTMEnabled(gtmEnabled);
  }, [analyticsEnabled, gtmEnabled]);

  useEffect(() => {
    const changed = localAnalyticsEnabled !== analyticsEnabled || localGTMEnabled !== gtmEnabled;
    setHasChanges(changed);
  }, [localAnalyticsEnabled, localGTMEnabled, analyticsEnabled, gtmEnabled]);

  const handleAnalyticsToggle = (e) => {
    setLocalAnalyticsEnabled(e.target.checked);
  };

  const handleGTMToggle = (e) => {
    setLocalGTMEnabled(e.target.checked);
  };

  const handleSave = () => {
    // Update Redux store
    dispatch(updateCookiePreferences({
      analyticsEnabled: localAnalyticsEnabled,
      gtmEnabled: localGTMEnabled,
    }));

    // Apply analytics settings
    initializeAnalytics(localAnalyticsEnabled, localGTMEnabled);

    // If disabling both, clear cookies
    if (!localAnalyticsEnabled && !localGTMEnabled) {
      clearAllAnalyticsCookies();
    }

    // Show success message
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    setHasChanges(false);
  };

  const handleAcceptAll = () => {
    setLocalAnalyticsEnabled(true);
    setLocalGTMEnabled(true);
    
    dispatch(updateCookiePreferences({
      analyticsEnabled: true,
      gtmEnabled: true,
    }));

    initializeAnalytics(true, true);

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    setHasChanges(false);
  };

  const handleRejectAll = () => {
    setLocalAnalyticsEnabled(false);
    setLocalGTMEnabled(false);
    
    dispatch(updateCookiePreferences({
      analyticsEnabled: false,
      gtmEnabled: false,
    }));

    initializeAnalytics(false, false);
    clearAllAnalyticsCookies();

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    setHasChanges(false);
  };

  return (
    <div className='bg-custom-charcoal/50 p-6 rounded-lg mt-6'>
      <h3 className='text-xl font-semibold text-white mb-4'>Manage Your Cookie Preferences</h3>
      
      {showSuccess && (
        <div className='bg-green-900/30 border border-green-500/50 text-green-300 px-4 py-3 rounded mb-4'>
          ✓ Your cookie preferences have been saved successfully.
        </div>
      )}

      <p className='text-gray-300 mb-6'>
        You can control which optional cookies you want to allow. Essential cookies cannot be disabled 
        as they are required for the app to function.
      </p>

      {/* Essential Cookies - Always On */}
      <div className='mb-6 pb-6 border-b border-gray-600'>
        <div className='flex justify-between items-start mb-2'>
          <div className='flex-1'>
            <h4 className='text-lg font-semibold text-white mb-1'>Essential Cookies</h4>
            <p className='text-sm text-gray-400'>
              Required for authentication and app functionality. These cannot be disabled.
            </p>
          </div>
          <div className='ml-4'>
            <div className='bg-gray-600 rounded-full w-12 h-6 flex items-center px-1'>
              <div className='bg-white rounded-full w-5 h-5 ml-auto'></div>
            </div>
            <span className='text-xs text-gray-400 block mt-1'>Always On</span>
          </div>
        </div>
      </div>

      {/* Google Analytics */}
      <div className='mb-6 pb-6 border-b border-gray-600'>
        <div className='flex justify-between items-start mb-2'>
          <div className='flex-1'>
            <h4 className='text-lg font-semibold text-white mb-1'>Google Analytics</h4>
            <p className='text-sm text-gray-400 mb-2'>
              Helps us understand how users interact with the app. Collects anonymized data about 
              page views, user behavior, and device information.
            </p>
            <p className='text-xs text-gray-500'>
              Cookies: <code className='bg-custom-charcoal px-2 py-1 rounded'>_ga</code>, 
              <code className='bg-custom-charcoal px-2 py-1 rounded ml-1'>_gid</code>, 
              <code className='bg-custom-charcoal px-2 py-1 rounded ml-1'>_gat</code>
            </p>
          </div>
          <div className='ml-4'>
            <label className='relative inline-block w-12 h-6 cursor-pointer'>
              <input
                type='checkbox'
                checked={localAnalyticsEnabled}
                onChange={handleAnalyticsToggle}
                className='sr-only peer'
              />
              <div className={`w-12 h-6 rounded-full transition-colors ${
                localAnalyticsEnabled ? 'bg-custom-light-blue' : 'bg-gray-600'
              }`}>
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  localAnalyticsEnabled ? 'translate-x-6' : 'translate-x-0'
                }`}></div>
              </div>
            </label>
            <span className='text-xs text-gray-400 block mt-1'>
              {localAnalyticsEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      {/* Google Tag Manager */}
      <div className='mb-6'>
        <div className='flex justify-between items-start mb-2'>
          <div className='flex-1'>
            <h4 className='text-lg font-semibold text-white mb-1'>Google Tag Manager</h4>
            <p className='text-sm text-gray-400 mb-2'>
              Manages and deploys marketing and analytics tags. Helps us track user interactions 
              and optimize the app experience.
            </p>
            <p className='text-xs text-gray-500'>
              Used for: Tag management, event tracking, conversion tracking
            </p>
          </div>
          <div className='ml-4'>
            <label className='relative inline-block w-12 h-6 cursor-pointer'>
              <input
                type='checkbox'
                checked={localGTMEnabled}
                onChange={handleGTMToggle}
                className='sr-only peer'
              />
              <div className={`w-12 h-6 rounded-full transition-colors ${
                localGTMEnabled ? 'bg-custom-light-blue' : 'bg-gray-600'
              }`}>
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  localGTMEnabled ? 'translate-x-6' : 'translate-x-0'
                }`}></div>
              </div>
            </label>
            <span className='text-xs text-gray-400 block mt-1'>
              {localGTMEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className='flex flex-wrap gap-3 mt-6'>
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          className={`px-6 py-2 rounded font-medium transition-colors ${
            hasChanges 
              ? 'bg-custom-light-blue text-white hover:bg-blue-600' 
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          Save Preferences
        </button>
        
        <button
          onClick={handleAcceptAll}
          className='px-6 py-2 rounded font-medium bg-green-700 text-white hover:bg-green-600 transition-colors'
        >
          Accept All
        </button>
        
        <button
          onClick={handleRejectAll}
          className='px-6 py-2 rounded font-medium bg-red-700 text-white hover:bg-red-600 transition-colors'
        >
          Reject All Optional
        </button>
      </div>

      <p className='text-xs text-gray-500 mt-4'>
        Note: Changes will take effect immediately. If you disable analytics cookies, existing cookies 
        will be deleted and no new tracking data will be collected.
      </p>
    </div>
  );
};

export default CookiePreferences;
